// Historical Performance card. Preserves the P0 historical-data
// accuracy protections:
//   * L5 / L10 / Season / Season Avg
//   * games_available badge
//   * "Latest game included: {date}" freshness label
//   * amber stale-data notice (historical_data_stale)
// No chronology math is performed here -- the backend is the source of
// truth. This card is presentation-only.
//
// V1.1: Composes the market-aware ``RecentGames`` visualization below
// the aggregate metrics. The visualization is descriptive only and
// consumes the same ``historical.recent_game_logs`` rows already
// returned by ``compute_historical`` -- zero incremental provider calls.
import { RecentGames } from "./RecentGames";

export function HistoricalPerformanceCard({ historical, market, side, line }) {
  if (!historical) {
    return (
      <section
        data-testid="analyzer-data-check-historical"
        className="pa-card p-6 md:p-8 mt-6 md:mt-8"
      >
        <h3 className="font-display text-xl md:text-2xl uppercase tracking-wide text-slate-900">
          Historical Performance
        </h3>
        <p
          className="mt-2 text-sm text-slate-600"
          data-testid="analyzer-data-check-historical-empty"
        >
          Historical stats not connected yet.
        </p>
      </section>
    );
  }

  const fmtRate = (r) =>
    typeof r === "number" ? `${Math.round(r * 100)}%` : "—";
  const fmtAvg = (a) => (typeof a === "number" ? a.toFixed(2) : "—");
  const fmtLatestDate = (iso) => {
    if (typeof iso !== "string" || !iso) return null;
    try {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return null;
      return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    } catch {
      return null;
    }
  };

  const games = historical.games_available || 0;
  const l5Sample = Math.min(5, games);
  const l10Sample = Math.min(10, games);
  const isStale = historical.historical_data_stale === true;
  const latestLabel = fmtLatestDate(historical.historical_latest_game_date);

  const l5Pushes = historical.last_5_pushes || 0;
  const l10Pushes = historical.last_10_pushes || 0;
  const seasonPushes = historical.season_pushes || 0;
  const pushLabel = (n) =>
    n > 0 ? `${n} push${n === 1 ? "" : "es"}` : null;

  const sampleLabel = (target, actual) => {
    if (actual === 0) return "no games available";
    if (actual < target) return `only ${actual} game${actual === 1 ? "" : "s"} available`;
    return `${actual} of ${target}`;
  };

  return (
    <section
      data-testid="analyzer-data-check-historical"
      className="pa-card p-6 md:p-8 mt-6 md:mt-8"
    >
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <h3 className="font-display text-xl md:text-2xl uppercase tracking-wide text-slate-900">
          Historical Performance
        </h3>
        <p
          className="text-xs text-slate-500 font-mono-num"
          data-testid="analyzer-data-check-historical-provider"
        >
          {historical.provider || "BALLDONTLIE"} ·{" "}
          <span data-testid="analyzer-data-check-games-available">
            {games} game{games === 1 ? "" : "s"} available
          </span>
        </p>
      </div>

      {isStale ? (
        <div
          className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
          data-testid="analyzer-data-check-historical-stale"
        >
          Historical data is still updating. PA is temporarily withholding this
          score rather than grading with incomplete recent results.
        </div>
      ) : latestLabel ? (
        <p
          className="mt-2 text-[11px] text-slate-500"
          data-testid="analyzer-data-check-historical-latest-game"
        >
          Latest game included: {latestLabel}
        </p>
      ) : null}

      <dl className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-3">
        <div>
          <dt className="text-[11px] uppercase tracking-wide text-slate-500 font-display">
            Last 5
          </dt>
          <dd
            className="text-slate-900 text-sm font-mono-num"
            data-testid="dc-l5"
          >
            {fmtRate(historical.last_5_hit_rate)}
          </dd>
          <p
            className="text-[10px] text-slate-500"
            data-testid="dc-l5-sample"
          >
            {sampleLabel(5, l5Sample)}
          </p>
          {pushLabel(l5Pushes) && (
            <p
              className="text-[10px] text-amber-700"
              data-testid="dc-l5-pushes"
            >
              {pushLabel(l5Pushes)}
            </p>
          )}
        </div>
        <div>
          <dt className="text-[11px] uppercase tracking-wide text-slate-500 font-display">
            Last 10
          </dt>
          <dd
            className="text-slate-900 text-sm font-mono-num"
            data-testid="dc-l10"
          >
            {fmtRate(historical.last_10_hit_rate)}
          </dd>
          <p
            className="text-[10px] text-slate-500"
            data-testid="dc-l10-sample"
          >
            {sampleLabel(10, l10Sample)}
          </p>
          {pushLabel(l10Pushes) && (
            <p
              className="text-[10px] text-amber-700"
              data-testid="dc-l10-pushes"
            >
              {pushLabel(l10Pushes)}
            </p>
          )}
        </div>
        <div>
          <dt className="text-[11px] uppercase tracking-wide text-slate-500 font-display">
            Season
          </dt>
          <dd
            className="text-slate-900 text-sm font-mono-num"
            data-testid="dc-season"
          >
            {fmtRate(historical.season_hit_rate)}
          </dd>
          <p
            className="text-[10px] text-slate-500"
            data-testid="dc-season-sample"
          >
            {games} game{games === 1 ? "" : "s"}
          </p>
          {pushLabel(seasonPushes) && (
            <p
              className="text-[10px] text-amber-700"
              data-testid="dc-season-pushes"
            >
              {pushLabel(seasonPushes)}
            </p>
          )}
        </div>
        <div>
          <dt className="text-[11px] uppercase tracking-wide text-slate-500 font-display">
            Season Avg
          </dt>
          <dd
            className="text-slate-900 text-sm font-mono-num"
            data-testid="dc-season-avg"
          >
            {fmtAvg(historical.season_average)}
          </dd>
        </div>
      </dl>

      <RecentGames
        historical={historical}
        market={market}
        side={side}
        line={line}
      />
    </section>
  );
}
